/*
    Created by Phat Pham
    phat@launchdeck.org
*/

import React, {
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import {
    View,
    Text,
    Image, useWindowDimensions,
} from 'react-native-web';
import * as Braze from '@braze/web-sdk';
import PropTypes from 'prop-types';
import * as Animatable from 'react-native-animatable';

// Custom Components
import { Button } from '../../components';

// Configs
import GLOBAL from '../../configs/config_global';

// Utils
import { showAlert } from '../../utils/util_helpers';

const BrazeBanner = React.forwardRef((props, ref) => {
    const { width } = useWindowDimensions();

    // region Props Destructuring
    const {
        isLoading
    } = props;
    // endregion

    // region Refs
    const cardRefreshListener = useRef(null);
    // endregion

    // region Local State
    const [cardList, setCardList] = useState([]);
    // endregion

    // region Component Mounting
    useEffect(() => {
        getCardList();

        // Listen for updates as a result of card refreshes
        cardRefreshListener.current = Braze.subscribeToContentCardsUpdates((data) => getCardList(data.cards));

        // Component Will Unmount
        return(() => Braze.removeSubscription(cardRefreshListener.current));
    }, []);
    // endregion

    // region Function - Get Content Cards List
    const getCardList = (customCardList = null) => {
        const usingCardList = customCardList || Braze.getCachedContentCards()?.cards;
        console.log('[INFO] Braze Content Cards', usingCardList);

        // Get only the pinned one
        const newCardList = (usingCardList || [])
            .filter((item) => item.pinned && !item.dismissed)
            .map((item) => ({
                ...item,
                hasBanner: !!item.aspectRatio,
                hasLink: !!item.url?.trim()
            }));

        // Save to state
        setCardList(process.env.NODE_ENV !== 'production' ? newCardList : (newCardList.length ? [newCardList[0]] : []));
    };
    // endregion

    // region Function - Mark Card Done
    const markCardDone = (cardData) => {
        if (!cardData) return;
        showAlert({
            text: `Mark this notification as ${cardData.hasLink ? 'done' : 'read'}?`,
            confirmButton: {
                text: 'Confirm',
                color: GLOBAL.COLOR.RED,
                onPress: () => {
                    Braze.logContentCardImpressions([cardData]);
                    Braze.logCardDismissal(cardData);
                    setTimeout(() => Braze.requestContentCardsRefresh(), 500);
                    setCardList(cardList.filter((item) => item.id !== cardData.id));
                }
            },
            cancelButton: { text: 'Cancel' }
        });
    };
    // endregion

    // region Function - Execute Card Action
    const executeCardAction = (cardData = {}) => {
        window.open(cardData.url, '_blank');
    };
    // endregion

    // region Expose Methods
    useImperativeHandle(ref, () => ({ refreshCardList: () => getCardList() }), []);
    // endregion

    // region MAIN RENDER
    return(
        isLoading ?
            false
            :
            cardList.map((cardData) => {
                const IMAGE_DEFAULT_HEIGHT = 88;
                const IMAGE_WIDTH = cardData.hasBanner ? (width - 32) : (IMAGE_DEFAULT_HEIGHT * (cardData.aspectRatio || 1));
                return(
                    <Animatable.View
                        key={cardData.id}
                        animation={'fadeZoom'}
                        duration={600}
                        style={{ width: '100%', padding: 16, borderBottomWidth: 1, borderColor: GLOBAL.COLOR.DIVIDER, backgroundColor: 'white' }}>
                        {/* Main View */}
                        <View style={{ alignSelf: 'stretch' }}>
                            {/* Image and Text */}
                            <View style={Object.assign({ width: '100%' }, cardData.hasBanner ? {} : { flexDirection: 'row', alignItems: 'flex-start' })}>
                                {/* Image */}
                                {
                                    (cardData.image || cardData.imageUrl) &&
                                    <Image
                                        source={{ uri: cardData.image || cardData.imageUrl }}
                                        style={{ width: IMAGE_WIDTH, height: 200, backgroundColor: GLOBAL.COLOR.BASIC.NEUTRAL_LIGHT_GREY }}
                                        resizeMode={'cover'}
                                    />
                                }

                                {/* Text View */}
                                {
                                    (!!cardData.title?.trim() || !!cardData.description?.trim()) &&
                                    <View style={cardData.hasBanner ? { width: '100%', paddingTop: 24, paddingBottom: 8 } : { flex: 1, padding: 24, paddingTop: 0 }}>
                                        {/* Title */}
                                        {
                                            !!cardData.title?.trim() &&
                                            <Text style={{ fontWeight: 'bold', fontSize: 24, alignSelf: 'stretch' }} numberOfLines={1}>
                                                {cardData.title}
                                            </Text>
                                        }

                                        {/* Description */}
                                        <Text style={{ fontSize: 18, marginTop: 8 }}>
                                            {cardData.description || ''}
                                        </Text>
                                    </View>
                                }
                            </View>

                            {/* Button Container */}
                            <View style={{ width: '100%', flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
                                {/* Dismiss Button */}
                                <Button
                                    style={{ marginRight: cardData.hasLink ? 4 : 0, flex: 1 }}
                                    text={cardData.hasLink ? 'Mark as Done' : 'Got it'}
                                    textStyle={{ color: 'white' }}
                                    onPress={() => markCardDone(cardData)}
                                />

                                {/* Open Link Button */}
                                {
                                    cardData.hasLink &&
                                    <Button
                                        style={{ marginLeft: 4, flex: 1, backgroundColor: GLOBAL.COLOR.BASIC.UI_BLUE }}
                                        text={cardData.domain || 'Open Page'}
                                        textStyle={{ color: 'white' }}
                                        onPress={() => executeCardAction(cardData)}
                                    />
                                }
                            </View>
                        </View>
                    </Animatable.View>
                );
            })
    );
    // endregion
});

// region Props
BrazeBanner.propTypes = {
    isLoading: PropTypes.bool
};

BrazeBanner.defaultProps = {
    isLoading: false
};
// endregion

export default BrazeBanner;
